import {Box, Col, css, Row} from "@cdx/ds";
import {DragController, Draggable, useDragStore} from "@codecks/dnd";
import {
  AllOrders,
  ArenaCtx,
  CardPanelBag,
  SwimlaneDropArea,
  SwimlaneGroup,
} from "./card-panel-utils";
import {useCardContainer} from "../card-selection/useCardContainer";
import SwimlaneLabel, {useHoverWaiter} from "./SwimlaneLabel";
import {
  useRef,
  type ReactNode,
  useState,
  MouseEventHandler,
  forwardRef,
  CSSProperties,
  useLayoutEffect,
  cloneElement,
  useMemo,
  ReactElement,
  useEffect,
} from "react";
import RAW_ORDERS from "../card-order/order-info";
import {
  Range,
  VirtualItem,
  Virtualizer,
  defaultRangeExtractor,
  observeElementRect,
  observeWindowRect,
  useVirtualizer,
} from "@tanstack/react-virtual";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {cx, springConfigs, useGetNodeRefFromRef} from "@cdx/common";
import {getStatusForCard} from "../../lib/card-status-utils";
import {tableViewStyles} from "./card-panel.css";
import {Link} from "react-router-dom";
import {
  CheckListCell,
  DeckCell,
  DueDateCell,
  EffortCell,
  MiscCell,
  MsCell,
  MultiDragHint,
  OwnerCell,
  PrioCell,
  SelectionBox,
  SprintCell,
  StatusRect,
  TableViewConvoHint,
  TagCell,
  TimeTrackedCell,
  TitleCell,
  UpvotesCell,
  getForceShowMiscCell,
  preloadCardCellInfo,
  showCheckListCell,
  showDueDateCell,
  showMilestoneCell,
  showOwnerCell,
  showSprintCell,
  showTagsCell,
  showTimeTrackingCell,
  showUpvotesCell,
} from "./TableViewCells";
import {CardCursor, useTableViewNavigation} from "./mini-card-navigation-shortcuts";
import {useCardHoverHandlersWithSelection} from "../../components/Card/useHoverShortcuts";
import {
  cardSelectionActions,
  getSelectedCardIds,
  useSelectedCardIds,
  useSelectionStore,
} from "../card-selection/useSelectedCards";
import uiClasses from "@cdx/common/xui/ui.css";
import {checkIfPresent, useInstance} from "../../lib/mate/mate-utils";
import {api} from "../../lib/api";
import {useSpring, animated} from "react-spring";
import {getCardResizeFactor} from "../../components/Card/dragged-card-resize";
import dsStyles from "@cdx/ds/css/index.css";
import {allCardsWithOrderKey, useCardDropTarget} from "./CardDropSidebar";
import {mergeRefs} from "react-merge-refs";
import {QuickAddButton} from "./QuickAddButton";
import {useDSDimensions} from "@cdx/ds/hooks/position-hooks";
import {Card, CardId} from "../../cdx-models/Card";
import {hasPermissionToModifyCard} from "../../lib/permissions";
import {Root} from "../../cdx-models/Root";
import {getCardOrderInfo} from "./ZoneComponents";
import {transitions} from "@cdx/ds/css/decoration.css";

const ORDERS = RAW_ORDERS as any as AllOrders;

type SwimlaneLayoutProps = {
  group: SwimlaneGroup;
  bag: CardPanelBag;
  cardContainerKey: string;
  children: ReactNode;
  quickAddInfo: any;
};
const SwimlaneLayout = (props: SwimlaneLayoutProps) => {
  const {group, bag, cardContainerKey, children, quickAddInfo} = props;
  const {handlers: waitHandlers, waited} = useHoverWaiter();
  const groupOrderInfo = group.orderProp ? ORDERS[group.orderProp] : null;

  const searchLabelFilter =
    groupOrderInfo?.getLabelSearchFilter && groupOrderInfo.getLabelSearchFilter(group.keyAsValue);

  const {dragItem, dropRef, isOver} = useCardDropTarget({
    orderKeyAsValue: group.keyAsValue,
    orderKeyAsScalar: group.scalarKey,
    orderInfo: groupOrderInfo,
    bag: bag,
  } as any);

  const dragCards = dragItem && dragItem.data.cardInfos.map(({getCard}: any) => getCard());
  const allCardsFromHere =
    dragItem &&
    allCardsWithOrderKey({
      cards: dragCards,
      orderInfo: groupOrderInfo,
      orderKeyAsScalar: group.scalarKey,
      projects: bag.projects,
      root: bag.root,
      notifications: bag.notifications,
    });

  return (
    <Col sp="12px" {...waitHandlers} ref={dropRef} relative className={uiClasses.hideContainer}>
      <SwimlaneLabel
        cards={group.cardsWithKeys.map(({card}) => card)}
        waitedForSelection={Boolean(waited)}
        orderProp={group.orderProp}
        isDraggingCardIds={bag.isDraggingCardIds}
        searchLabelFilter={searchLabelFilter}
        onLight={bag.onLight}
        cardContainerKey={cardContainerKey}
        root={bag.root}
        addButton={
          quickAddInfo && <QuickAddButton targetProps={quickAddInfo} arenaCtx={bag.arenaCtx} />
        }
      >
        {groupOrderInfo?.getLabel(group.keyAsValue, {...bag, mode: "cardPanel"}) || null}
      </SwimlaneLabel>
      {dragItem && groupOrderInfo?.onDrop && !allCardsFromHere && (
        <SwimlaneDropArea
          dragItem={dragItem}
          orderInfo={groupOrderInfo}
          orderKeyAsValue={group.keyAsValue}
          label={groupOrderInfo?.getLabel(group.keyAsValue, {
            ...bag,
            mode: "dropOverlay",
            onLight: true,
          })}
          isOver={isOver}
        />
      )}
      {children}
    </Col>
  );
};

export const CARD_ROW_HEIGHT = 35;

const DragCardRow = ({
  card,
  multiDragCount,
  root,
}: {
  card: Card;
  multiDragCount: number | false;
  root: Root;
}) => {
  const status = getStatusForCard(card) as "hero";
  const isSelected = useSelectionStore((s) => s.selectedCardIds.has(card.cardId));
  return (
    <Row
      bg="foreground"
      align="center"
      className={cx(
        dsStyles.overflow.hidden,
        cardStatusVariants[status],
        tableViewStyles.cardRow.base,
        isSelected && tableViewStyles.cardRow.selected
      )}
      colorTheme={isSelected ? "active25" : undefined}
      px="8px"
    >
      {multiDragCount ? <MultiDragHint count={multiDragCount} /> : null}
      <TitleCell card={card} root={root} />
    </Row>
  );
};

type RawCardRowProps = {
  card: Card;
  cardId: CardId;
  bag: CardPanelBag;
  cardContainerKey: string;
  showColumns: ShowColumns;
  isSelecting?: boolean;
  isSelected?: boolean;
  className?: string;
  style?: CSSProperties;
};
const RawCardRow = forwardRef<HTMLAnchorElement, RawCardRowProps>((props, outerRef) => {
  const {
    card,
    cardId,
    bag,
    showColumns,
    cardContainerKey,
    isSelected,
    isSelecting,
    className,
    ...rest
  } = props;

  const {result: status, isLoaded: isStatusLoaded} = checkIfPresent(
    () => getStatusForCard(card) as "hero",
    api
  );
  const {activeCard, shownManualOrder, root} = bag;
  const isOpen = activeCard && activeCard.id === card.cardId;
  const canModify = Boolean(hasPermissionToModifyCard({root, card}));

  const nodeRef = useRef<HTMLElement | null>(null);
  const mergedRef = useMemo(() => mergeRefs([nodeRef, outerRef]), [outerRef]);

  const {waitedForSelection, events} = useCardHoverHandlersWithSelection({
    type: "MINI_CARD",
    isSelecting: Boolean(isSelecting),
    cardId,
    cardNodeRef: nodeRef,
    cardContainerKey,
  });

  const handleSelectionClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isSelected) {
      cardSelectionActions.removeSelectedCardIds([cardId], cardContainerKey);
    } else {
      cardSelectionActions.addSelectedCardIds([cardId], cardContainerKey);
    }
  };

  const zoneInfo = getCardOrderInfo(card, root.account);

  return (
    <Link
      to={bag.arenaCtx.routes.getCardUrl({card, cardContainerKey})}
      onClick={isSelecting ? handleSelectionClick : undefined}
      {...events}
      {...rest}
      ref={mergedRef}
      className={css(
        {
          display: "flex",
          bg: "foreground",
          align: "center",
          colorTheme: isSelected ? "active25" : undefined,
          px: "8px",
        },
        className,
        cardStatusVariants[status],
        tableViewStyles.cardRow.base,
        isOpen && tableViewStyles.cardRow.isOpen,
        isSelected && tableViewStyles.cardRow.selected,
        uiClasses.hideContainer
      )}
    >
      <SelectionBox
        isSelected={Boolean(isSelected)}
        isSelecting={Boolean(isSelecting)}
        hasWaited={waitedForSelection}
        onClick={handleSelectionClick}
      />
      <StatusRect status={status} isLoaded={isStatusLoaded} card={card} />
      {showColumns.priority && (
        <PrioCell
          card={card}
          canModify={!isSelecting && bag.canCreateCard}
          cardContainerKey={cardContainerKey}
        />
      )}
      {showColumns.effort && (
        <EffortCell
          card={card}
          canModify={!isSelecting && bag.canCreateCard}
          cardContainerKey={cardContainerKey}
          root={bag.root}
        />
      )}
      {showColumns.sprint && (
        <SprintCell
          card={card}
          canModify={canModify}
          cardContainerKey={cardContainerKey}
          zoneInfo={zoneInfo?.sprint}
        />
      )}
      {showColumns.milestone && (
        <MsCell
          card={card}
          root={bag.root}
          cardContainerKey={cardContainerKey}
          canModify={canModify}
          zoneInfo={zoneInfo?.milestone}
        />
      )}
      {showColumns.owner && (
        <OwnerCell card={card} canModify={canModify} cardContainerKey={cardContainerKey} />
      )}
      <TitleCell card={card} root={bag.root} />
      {showColumns.checkList && <CheckListCell card={card} />}
      {showColumns.deck && (
        <DeckCell card={card} canModify={canModify} cardContainerKey={cardContainerKey} />
      )}
      {showColumns.tags && <TagCell card={card} />}
      {showColumns.dueDate && <DueDateCell card={card} root={bag.root} />}
      {showColumns.timeTracking && <TimeTrackedCell card={card} root={bag.root} />}
      {showColumns.upvotes && <UpvotesCell card={card} root={bag.root} />}
      {showColumns.misc && (
        <MiscCell
          card={card}
          bag={bag}
          leftOverWidth={showColumns.leftOverWidth}
          zoneInfo={zoneInfo}
          shownManualOrder={shownManualOrder}
        />
      )}
      <TableViewConvoHint card={card} bag={bag} />
    </Link>
  );
});

type ItemRowProps = {
  items: RowItem[];
  virtualRow: VirtualItem<any>;
  offsetY?: number;
} & Omit<RawCardRowProps, "card" | "cardId">;
const ItemRow = (props: ItemRowProps) => {
  const {items, ...rest} = props;
  const {virtualRow} = rest;
  const item = items[virtualRow.index];
  if (item.type === "card") {
    return <CardRow {...rest} card={item.value} justDropped={item.justDropped} />;
  } else {
    return cloneElement(item.value, {
      className: cx(
        dsStyles.position.absolute,
        dsStyles.top[0],
        dsStyles.left[0],
        dsStyles.width["100%"],
        (item.value.props as any).className,
        transitions.transforms
      ),
      style: {
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start + (rest.offsetY ?? 0)}px)`,
      },
    } as any);
  }
};

type CardRowProps = {
  card: Card;
  virtualRow: VirtualItem<any>;
  offsetY?: number;
  justDropped?: boolean;
} & Omit<RawCardRowProps, "card" | "cardId">;
const CardRow = (props: CardRowProps) => {
  const {virtualRow, card, offsetY, justDropped, ...rest} = props;
  const {bag} = rest;
  const cardId = card.$meta.get("cardId", null);

  const nodeRef = useRef<HTMLAnchorElement | null>(null);
  const {isSelecting, isSelected} = useSelectionStore((s) => ({
    isSelecting: s.selectedCardIds.size > 0,
    isSelected: s.selectedCardIds.has(cardId),
  }));

  const getDragData = () => {
    const selectedCardIds = getSelectedCardIds();
    const cardIds = isSelected ? [...selectedCardIds] : [cardId];
    const item = {
      cardInfos: cardIds.map((id) => ({
        cardId: id,
        getCard: () => api.getModel({modelName: "card", id} as any),
      })),
      dragFrom: bag.arenaCtx.type,
      dragCtx: undefined,
      rect: nodeRef.current?.getBoundingClientRect(),
    };
    return item;
  };

  return (
    <Draggable
      type="CARD"
      id={cardId}
      itemData={getDragData}
      disabled={!bag.canCreateCard || !cardId || process.env.REACT_APP_MODE === "open"}
      mergeRef={nodeRef}
    >
      {({handlers, ref}: any) => (
        <RawCardRow
          style={{
            height: `${virtualRow.size - 1}px`,
            transform: `translateY(${virtualRow.start + (offsetY ?? 0)}px)`,
          }}
          className={cx(tableViewStyles.row.base, justDropped && tableViewStyles.row.justDropped)}
          {...handlers}
          ref={ref}
          {...rest}
          isSelecting={isSelecting}
          isSelected={isSelected}
          card={card}
          cardId={cardId}
        />
      )}
    </Draggable>
  );
};

export const offsetAwareObserveElement = <T extends Element>(
  instance: Virtualizer<T, any>,
  cb: (offset: number) => void
) => {
  const element = instance.scrollElement;
  if (!element) return;
  let lastDiff = 0;
  const handler = () => {
    const containerEl = (instance.options as any).containerElRef?.current;
    if (!containerEl) return;
    const diff = element.getBoundingClientRect().top - containerEl.getBoundingClientRect().top;
    if (diff !== lastDiff) {
      lastDiff = diff;
      cb(diff);
    }
  };

  const id = setInterval(handler, 250);
  element.addEventListener("scroll", handler, {passive: true});
  return () => {
    element.removeEventListener("scroll", handler);
    clearInterval(id);
  };
};

const offsetAwareObserveWindow = <T extends Element>(
  instance: Virtualizer<T, any>,
  cb: (offset: number) => void
) => {
  const handler = () => {
    const containerEl = (instance.options as any).containerElRef?.current;
    // containerEl might be null if the tableview unmounts
    if (!containerEl) return;
    cb(-containerEl.getBoundingClientRect().top);
  };
  handler();
  window.addEventListener("scroll", handler, {passive: true});
  return () => {
    window.removeEventListener("scroll", handler);
  };
};

const PAGE_SIZE = 64;

const createPageManager = () => {
  const loadedPages = new Set<number>();
  return {
    onLoadRange: (range: Range, items: RowItem[], bag: CardPanelBag) => {
      const startPage = Math.floor(range.startIndex / PAGE_SIZE);
      const endPage = Math.floor(range.endIndex / PAGE_SIZE);
      for (let p = startPage; p <= endPage; p += 1) {
        if (loadedPages.has(p)) continue;
        const end = Math.min(items.length, (p + 1) * PAGE_SIZE);
        for (let idx = p * PAGE_SIZE; idx < end; idx += 1) {
          const item = items[idx];
          if (item.type === "card") preloadCardCellInfo(item.value, bag);
        }
        loadedPages.add(p);
      }
    },
  };
};

const TargetIndicator = ({dragTargetIndex}: {dragTargetIndex: number}) => (
  <Box
    absolute
    top="0"
    left="0"
    width="100%"
    className={transitions.transforms}
    borderWidth={0}
    borderBottomWidth={1}
    borderColor="default"
    style={{
      zIndex: -1,
      transform: `translate(0, ${dragTargetIndex * CARD_ROW_HEIGHT - 1}px)`,
    }}
  />
);

type RowItem =
  | {type: "card"; value: any; id: CardId; justDropped?: boolean}
  | {type: "node"; value: ReactElement; id: string};

type CardTableProps = {
  items: RowItem[];
  bag: CardPanelBag;
  cardContainerKey: string;
  showColumns: ShowColumns;
  lastEl?: ReactElement;
  cursor: CardCursor | null;
  dragInfo?: {dragIndex: number; currIndex: number} | null;
};
export const CardTable = forwardRef<HTMLDivElement, CardTableProps>((props, passedRef) => {
  const {items: rawItems, bag, cardContainerKey, showColumns, lastEl, cursor, dragInfo} = props;
  const [pageManager] = useState(createPageManager);

  let items: RowItem[] = lastEl
    ? [...rawItems, {type: "node", value: lastEl, id: "$lastEl"}]
    : rawItems;

  const {nodeRef, ref} = useGetNodeRefFromRef(passedRef);

  const getCursorCardIdx = () => {
    if (!cursor) return null;
    if (cursor.cardContainerKey !== cardContainerKey) return null;
    if (cursor.cardId === null) return null;
    return cursor.cardIdx;
  };

  const cursorCardIdx = getCursorCardIdx();

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => bag.scrollElementRef.current,
    estimateSize: () => CARD_ROW_HEIGHT,
    overscan: 5,
    getItemKey: (idx: number) => items[idx].id,
    observeElementOffset:
      bag.scrollElementRef.current === window
        ? offsetAwareObserveWindow
        : offsetAwareObserveElement,
    ...({containerElRef: nodeRef} as any),
    observeElementRect:
      bag.scrollElementRef.current === window ? observeWindowRect : observeElementRect,
    scrollToFn: (...args) => {
      // we don't want it to scroll, as otherwise it'll auto scroll to the first item based on wrong offset information,
    },
    rangeExtractor: (range) => {
      if (range.startIndex === range.endIndex && range.count > 1) return [];
      pageManager.onLoadRange(range, items, bag);
      return defaultRangeExtractor(range);
    },
  });

  const scrollRefs = useRef({bag, cursorCardIdx});
  useEffect(() => {
    scrollRefs.current.bag = bag;
    scrollRefs.current.cursorCardIdx = cursorCardIdx;
  });

  useEffect(() => {
    if (cursorCardIdx !== null) {
      const scrollEl = scrollRefs.current.bag.scrollElementRef.current;
      const containerEl = nodeRef.current;
      if (nodeRef.current && scrollEl) {
        const getDiff = () => {
          const containerRect = containerEl.getBoundingClientRect();
          if (scrollEl === window) {
            return {
              offsetTop: containerRect.top + window.scrollY,
              viewportTop: window.scrollY,
              viewportBottom: window.innerHeight + window.scrollY,
            };
          } else {
            const scrollNode = scrollEl as HTMLElement;
            const scrollNodeRect = scrollNode.getBoundingClientRect();
            const offset = scrollNode.scrollTop - scrollNodeRect.top;
            return {
              viewportTop: scrollNodeRect.top + offset,
              viewportBottom: scrollNodeRect.bottom + offset,
              offsetTop: containerRect.top + offset,
            };
          }
        };
        const {offsetTop, viewportBottom, viewportTop} = getDiff();
        const nextTarget = cursorCardIdx * CARD_ROW_HEIGHT + offsetTop;

        if (nextTarget < viewportTop + 50 || nextTarget + CARD_ROW_HEIGHT > viewportBottom) {
          scrollEl.scrollTo({
            behavior: "smooth",
            top: nextTarget - (viewportBottom - viewportTop + CARD_ROW_HEIGHT) / 2,
          });
        }
      }
    }
  }, [cursorCardIdx, rowVirtualizer, nodeRef]);

  const isShown = items.length > 0 || bag.isDraggingCardIds.size > 0;

  const getTargetIndex = () => {
    if (!dragInfo) return null;
    const {currIndex, dragIndex} = dragInfo;
    if (currIndex > -1 && (currIndex === dragIndex || currIndex === dragIndex - 1)) return null;
    return dragIndex;
  };

  const targetIndex = getTargetIndex();

  return (
    <Box
      width="100%"
      relative
      zIndex={1}
      style={{
        minHeight: CARD_ROW_HEIGHT,
        height: `${rowVirtualizer.getTotalSize()}px`,
      }}
      elevation={isShown && 100}
      bg={isShown && "background"}
      rounded={4}
      colorTheme="gray25"
      ref={ref}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => (
        <ItemRow
          key={virtualRow.key}
          virtualRow={virtualRow}
          items={items}
          bag={bag}
          cardContainerKey={cardContainerKey}
          showColumns={showColumns}
          offsetY={targetIndex !== null ? (virtualRow.index < targetIndex ? -5 : 5) : 0}
        />
      ))}
      {targetIndex !== null && <TargetIndicator dragTargetIndex={targetIndex} />}
    </Box>
  );
});

type SwimlaneProps = {
  group: SwimlaneGroup;
  bag: CardPanelBag;
  idx: number;
  showColumns: ShowColumns;
  cursor: CardCursor | null;
};
const Swimlane = (props: SwimlaneProps) => {
  const {group, idx, bag, showColumns, cursor} = props;
  const {canCreateCard, arenaCtx} = bag;
  const {keyAsValue, groupOrderInfo} = group;

  const cardContainerKey = `lane-${group.scalarKey}`;

  const quickAddTargetProps =
    canCreateCard &&
    (arenaCtx.type === "deck" || arenaCtx.type === "milestone" || arenaCtx.type === "sprint") &&
    groupOrderInfo?.getQuickCreateProps?.(keyAsValue);

  useCardContainer({
    cardContainerKey,
    visibleCardIds: group.cardsWithKeys.map(({card}) => card.id),
    idx,
  } as any);

  return (
    <SwimlaneLayout
      group={group}
      bag={bag}
      cardContainerKey={cardContainerKey}
      quickAddInfo={quickAddTargetProps}
    >
      <CardTable
        items={group.cardsWithKeys.map(
          ({card}, cardIdx) =>
            ({
              type: "card",
              value: card,
              id: card.$meta.get("cardId", cardIdx),
            }) as RowItem
        )}
        bag={bag}
        cardContainerKey={cardContainerKey}
        showColumns={showColumns}
        cursor={cursor}
      />
    </SwimlaneLayout>
  );
};

const AnimatedDragCard = (props: {card: Card; multiDragCount: number; root: Root}) => {
  const {card, multiDragCount, root} = props;
  const [cardStyles, springApi] = useSpring(() => ({
    width: 300,
    scale: 1,
    translateX: 300,
    translateY: 0,
    config: springConfigs.quickNoBounce,
  }));

  useLayoutEffect(() => {
    let lastPos: null | {x: number; y: number} = null;
    let initialInfo: null | {dimensions: any; mouseOffset: any} = null;
    return useDragStore.subscribe(
      (state: any) => state.dragInfo,
      (dragInfo: any) => {
        if (dragInfo) {
          if (lastPos) {
            if (!initialInfo) {
              initialInfo = {
                dimensions: {...dragInfo.dimensions},
                mouseOffset: {...dragInfo.mouseOffset},
              };
            }
            // const dx = lastPos.x - dragInfo.currentPos.x;
            // const dy = lastPos.y - dragInfo.currentPos.y;
            const x = dragInfo.currentPos.x - dragInfo.mouseOffset.x;
            const y = dragInfo.currentPos.y - dragInfo.mouseOffset.y;
            const scale = Math.sqrt(getCardResizeFactor({x, y}));

            springApi.start({
              width: 300,
              // translateX: initialInfo.mouseOffset.x * (scale - 1),
              translateX: (((initialInfo.dimensions.width - 300) / 2) * 1) / scale,
              translateY: initialInfo.mouseOffset.y * (scale - 1),
              scale,
            });

            dragInfo.dimensions = {
              width: 300,
              height: 1 * initialInfo.dimensions.height,
            };

            // console.log({initialInfo});

            dragInfo.mouseOffset = {
              x: 0,
              y: scale * initialInfo.mouseOffset.y,
            };
          }
          lastPos = {x: dragInfo.currentPos.x, y: dragInfo.currentPos.y};
        }
      }
    );
  }, [springApi]);
  return (
    <div style={{perspective: "600px"}}>
      <animated.div style={cardStyles}>
        <DragCardRow card={card} multiDragCount={multiDragCount} root={root} />
      </animated.div>
    </div>
  );
};

export const DragRow = ({id, bag}: {id: CardId; bag: CardPanelBag}) => {
  const card = useInstance("card", id);
  const selectedCardIds = useSelectedCardIds();

  if (!card) return null;
  return (
    <AnimatedDragCard
      card={card}
      multiDragCount={selectedCardIds.size > 1 && selectedCardIds.size}
      root={bag.root}
    />
  );
};

export type ShowColumns = {
  effort: boolean;
  priority: boolean;
  milestone: boolean;
  sprint: boolean;
  owner: boolean;
  checkList: boolean;
  tags: boolean;
  deck: boolean;
  dueDate: boolean;
  timeTracking: boolean;
  upvotes: boolean;
  misc: boolean;
  leftOverWidth: number;
};

export const getShowColumns = (
  {cards, root, arenaCtx}: {cards: any[]; root: any; arenaCtx: ArenaCtx},
  width: number
): ShowColumns => {
  const minTitleWidth = 400;
  let leftOverWidth = width - (minTitleWidth + 100);
  return {
    effort: (leftOverWidth -= 36) > 0,
    priority: (leftOverWidth -= 36) > 0,
    owner: cards.some((c) => showOwnerCell(c)) && (leftOverWidth -= 36) > 0,
    sprint:
      arenaCtx.type !== "sprint" &&
      cards.some((c) => showSprintCell(c)) &&
      (leftOverWidth -= 36) > 0,
    milestone:
      arenaCtx.type !== "milestone" &&
      cards.some((c) => showMilestoneCell(c)) &&
      (leftOverWidth -= 80) > 0,
    deck: arenaCtx.type !== "deck" && (leftOverWidth -= 80) > 0,
    checkList: cards.some((c) => showCheckListCell(c)) && (leftOverWidth -= 60) > 0,
    tags: cards.some((c) => showTagsCell(c)) && (leftOverWidth -= 90) > 0,
    dueDate: cards.some((c) => showDueDateCell(c, root)) && (leftOverWidth -= 75) > 0,
    timeTracking: cards.some((c) => showTimeTrackingCell(c, root)) && (leftOverWidth -= 80) > 0,
    upvotes: cards.some((c) => showUpvotesCell(c, root)) && (leftOverWidth -= 55) > 0,
    misc: cards.some((c) => getForceShowMiscCell(c, root)) || leftOverWidth > 36,
    leftOverWidth: leftOverWidth,
  };
};

type TableViewProps = {
  groups: Map<string, SwimlaneGroup>;
  orderedGroupKeys: string[];
  bag: CardPanelBag;
};
const TableView = (props: TableViewProps) => {
  const {orderedGroupKeys, groups, bag} = props;
  const [dimRef, dims] = useDSDimensions();
  const showColumns = getShowColumns(bag, dims?.width ?? 800);

  const {activeCard, arenaCtx, location} = bag;

  const getCardIdsForGroup = (key: string) => {
    const entry = [...groups.entries()].find(([k]) => `lane-${k}` === key);
    return entry ? entry[1].cardsWithKeys.map(({card}) => card.id) : [];
  };

  const cursor = useTableViewNavigation({
    activeCard,
    getCardIdsForGroup,
    location,
    arenaCtx,
    groups: orderedGroupKeys.map((keyAsScalar) => `lane-${keyAsScalar}`),
  });

  return (
    <DragController type="CARD" renderItem={({id}: any) => <DragRow id={id} bag={bag} />}>
      <Col sp="32px" ref={dimRef}>
        {orderedGroupKeys.map((key, idx) => (
          <Swimlane
            key={`${key}:${idx}`}
            group={groups.get(key)!}
            bag={bag}
            idx={idx}
            showColumns={showColumns}
            cursor={cursor}
          />
        ))}
      </Col>
    </DragController>
  );
};

export default TableView;
